import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import aboutUsImg from '../assets/about-us.jpeg';
import { supabase } from '../lib/supabaseClient';
import { defaultContent } from '../lib/defaultContent';
import { useRegisterEditor } from '../lib/EditorContext';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

export default function About() {
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchContent() {
      try {
        const { data, error } = await supabase
          .from('page_content')
          .select('content')
          .eq('page_id', 'about')
          .single();

        if (error) {
          console.warn('Could not fetch about content, using defaults.', error.message);
        } else if (data && data.content) {
          setContent(data.content);
        }
      } catch (err) {
        console.error('Error fetching about content:', err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchContent();
  }, []);

  const title = content?.title || defaultContent.about.title;
  const missionParagraphs = content?.missionParagraphs || defaultContent.about.missionParagraphs;
  const whoWeAreTitle = content?.whoWeAreTitle || defaultContent.about.whoWeAreTitle;
  const bios = content?.bios || defaultContent.about.bios;
  const currentAboutUsImg = content?.aboutUsImg || aboutUsImg;

  const handleSaveSuccess = (newContent) => {
    setContent(newContent);
  };

  useRegisterEditor(
    'about',
    { title, missionParagraphs, whoWeAreTitle, bios, aboutUsImg: currentAboutUsImg },
    handleSaveSuccess,
    'About Page'
  );

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <motion.div initial="hidden" animate="visible" exit="hidden" className="py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h1 className="text-4xl md:text-5xl  font-bold mb-6 text-[#333] dark:text-[#fff]">{title}</h1>

          <div className="space-y-6 text-lg leading-relaxed text-left font-medium bg-sand-50 dark:bg-brand-slate/80 p-8 md:p-12 rounded-[2rem] border-2 border-brand-rose/20 shadow-lg relative">
            <div className="absolute top-0 right-0 -mt-6 -mr-6 text-4xl transform rotate-12"></div>
            {missionParagraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>

        <div className="space-y-24">
          <motion.div variants={fadeUp} className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="relative sticky top-8">
               <div className="absolute inset-0 bg-brand-rose opacity-20 rounded-[2rem] transform -rotate-3 scale-105 -z-10"></div>
              <img 
                src={currentAboutUsImg}
                alt="About Us"
                className="rounded-[2rem] shadow-xl object-cover w-full h-auto max-h-[700px]"
              />
              <div className="absolute -bottom-6 -right-6 text-5xl transform -rotate-12">❤️</div>
            </div>

            <div className="space-y-12">
              <div>
                <h2 className="text-4xl  font-bold mb-8 text-[#333] dark:text-[#fff]">{whoWeAreTitle}</h2>

                {bios.map((bio, index) => (
                  <div key={index} className="mb-12">
                    <h3 className="text-2xl font-bold mb-4 text-brand-rose">{bio.name}</h3>
                    <p className="font-semibold text-sm mb-4 uppercase tracking-wide text-brand-clay">{bio.credentials}</p>
                    <div className="space-y-4 leading-relaxed font-light">
                      {bio.paragraphs && bio.paragraphs.map((p, idx) => {
                        // We check if this paragraph is exactly where the quote used to be rendered.
                        // For a robust dynamic approach, we just output the quote if it exists.
                        // Let's render the quote after the first paragraph if there's one, or wherever it's specified.
                        return (
                          <React.Fragment key={idx}>
                            <p>{p}</p>
                            {idx === 0 && bio.quote && (
                              <blockquote className="border-l-4 border-brand-rose pl-4 italic font-medium my-6">
                                {bio.quote}
                              </blockquote>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                ))}

              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
